import { createContext, useCallback, useContext, useRef, useState } from "react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const ConfirmContext = createContext(null);

/**
 * App-wide confirmation dialog, mounted once in the page wrapper.
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title: "Remove Sarah T.?", body: "…", confirmLabel: "Remove", destructive: true }))) return;
 *
 * Replaces window.confirm(): it says what will happen, names the action
 * on the button, and is styled and keyboard-accessible.
 */
export function ConfirmProvider({ children }) {
  const [request, setRequest] = useState(null);
  const resolver = useRef(null);

  const confirm = useCallback((options) => new Promise((resolve) => {
    resolver.current = resolve;
    setRequest(options);
  }), []);

  const settle = (answer) => {
    resolver.current?.(answer);
    resolver.current = null;
    setRequest(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={!!request} onClose={() => settle(false)} title={request?.title} description={request?.body}>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          {/* Destructive actions start on Cancel, so Enter can't do harm. */}
          <Button variant="outline" onClick={() => settle(false)} data-autofocus={request?.destructive ? "" : undefined}>{request?.cancelLabel || "Cancel"}</Button>
          <Button variant={request?.destructive ? "destructive" : "default"} onClick={() => settle(true)} data-autofocus={request?.destructive ? undefined : ""}>
            {request?.confirmLabel || "Confirm"}
          </Button>
        </div>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const confirm = useContext(ConfirmContext);
  // Outside the provider (e.g. help-centre previews) fall back to the browser.
  return confirm || ((options) => Promise.resolve(window.confirm([options.title, options.body].filter(Boolean).join("\n\n"))));
}
